<template>
    <Sortable
        :list="items"
        item-key="_id"
        tag="div"
        :group="field.group ?? null"
        class="forms__field--repeater__sortable"
        @end="(e) => emit('sort-end', e)"
        @add="(e, item) => emit('sort-add', e, item)"
    >
        <template #empty>
            <div class="forms__field--repeater__empty-wrapper">
                <div class="forms__field--repeater__empty-inside">
                    <span>{{ lang('fields::fields.repeater.empty') }}</span>
                </div>
            </div>
        </template>
        <template #item="{ element, index }">
            <div
                :key="`${element._id}-${index}`"
                class="forms__field--repeater-block__item"
            >
                <header
                    class="forms__field--repeater-block__item__header"
                    :class="{ 'forms__field--repeater-block__item__header--expanded': expandedItems[element._id] }"
                    @click="toggleItem(element._id)"
                >
                    <div class="forms__field--repeater-block__item__header__drag cursor-grab">
                        <icon
                            icon="hero/solid/ellipsis-vertical"
                            class="w-3 h-3"
                        ></icon>
                    </div>
                    <span class="forms__field--repeater-block__item__header__index">
                        {{ index + 1 }}
                    </span>
                    <div v-for="header in headers" :key="header.slug" class="forms__field--repeater-block__item__header__field">
                        <div v-if="header.type === 'file'">
                            <img
                                v-if="element[header.slug] && element[header.slug].is_image"
                                :src="element[header.slug].url"
                                alt="file"
                                class="forms__field--repeater-block__item__header__field__image"
                            />
                        </div>
                        <div v-else-if="header.type === 'price_cents'">
                            {{ element[header.slug] / 100 }}
                        </div>
                        <div v-else-if="header.type === 'select'">
                            {{ getOptionsValue(header, element) }}
                        </div>
                        <div v-else>
                            {{ element[header.slug] }}
                        </div>
                    </div>
                    <icon
                        icon="hero/outline/chevron-down"
                        class="forms__field--repeater-block__item__header__chevron"
                        :class="{ 'transform rotate-180': expandedItems[element._id] }"
                    ></icon>
                </header>
                <div
                    v-show="expandedItems[element._id]"
                    class="forms__field--repeater-block__item__body"
                >
                    <template v-for="subfield in field.fields" :key="subfield.slug">
                        <Field
                            v-if="!subfield.condition ? true : solveCondition(subfield.condition, element)"
                            :id="`${field.slug ?? ''}${subfield.slug}-${element._id ?? index}`"
                            v-model="element[subfield.slug]"
                            :field="subfield"
                            :file="subfield.type === 'file' ? element[subfield.slug] : null"
                            :dusk="`form-${subfield.slug}`"
                            :tabindex="tabindex"
                            :options="subfield.options ? subfield.options : null"
                            :multiple="numberToBoolean(multiple)"
                            :label-position="subfield.labelPosition ? subfield.labelPosition : (labelPosition ?? 'left')"
                            :width="subfield.width"
                            :searchable="numberToBoolean(subfield.searchable)"
                            :size="size"
                            :repeater-element="element"
                        />
                    </template>

                    <slot name="subfield-after" :field="field" :value="element" :index="index"></slot>

                    <div class="forms__field--repeater-block__item__actions">
                        <Button type="button" size="sm" dusk="repeater-remove" @click="emit('remove', index)">
                            <icon
                                :icon="minusIcon ?? 'hero/solid/minus-sm'"
                                class="w-5 h-5"
                            ></icon>
                        </Button>
                    </div>
                </div>
            </div>
        </template>
    </Sortable>
</template>

<script lang="ts" setup>
import { solveCondition } from '@src/Fields/Pages/conditions';
import { computed, reactive } from 'vue';
import Sortable from '@src/Common/Components/Sortable.vue';

const props = defineProps<{
    field: any;
    items: any[];
    tabindex?: number | string | null;
    multiple?: boolean;
    inline?: boolean;
    size?: string;
    labelPosition?: string;
    minusIcon?: string;
}>();

const emit = defineEmits<{
    (event: 'remove', index: number): void;
    (event: 'sort-end', e: any): void;
    (event: 'sort-add', e: any, item: any): void;
}>();

const expandedItems = reactive<Record<string, boolean>>({});

const toggleItem = (id: string) => {
    expandedItems[id] = !expandedItems[id];
};

const expand = (id: string) => {
    expandedItems[id] = true;
};

const headersDefined = computed(() =>
    !props.field.fields ? [] : Object.values(props.field.fields).filter((item: any) => item.display_repeater_header),
);

const headers = computed(() =>
    headersDefined.value.length > 0 ? headersDefined.value : Object.values(props.field.fields ?? {}).slice(0, 3),
);

const getOptionsValue = (header: any, element: any) => {
    if (!header || !header.slug) {
        return '';
    }

    const selected = element[header.slug] ?? '';

    if (!selected) {
        return '';
    }

    const found = header.options?.find((option: any) => option.id === selected);

    return found ? found.name : '';
};

 const numberToBoolean = (input: number | boolean | string | undefined) => {
    if (typeof input === 'undefined') {
        return false;
    }
    if (typeof input === 'boolean') {
        return input;
    }
    if (typeof input === 'string') {
        return input === '1';
    }
    return input === 1;
};

defineExpose({ expand });
</script>
